import styled, { keyframes } from 'styled-components';

const breathe = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translate(-50%, 6px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const travel = keyframes`
  0% { left: -34%; }
  100% { left: 100%; }
`;

/* The chrome is outside the `dx-v2` scope, where the `--dx-*` tokens resolve to nothing. */
export const Band = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 16px;
  flex-shrink: 0;
  font-size: 13px;
  color: light-dark(#343a40, var(--mantine-color-dark-0));
  background: light-dark(#e7f5ff, var(--mantine-color-blue-light));
  border-bottom: 1px solid light-dark(#d0ebff, var(--mantine-color-blue-light));
  position: sticky;
  overflow: hidden;
  /* Directly under the AppTopBar, and the two bars below it move down by exactly this height. */
  top: 47px;
  z-index: 10;

  &[data-tone='waiting'] {
    background: light-dark(#fff9db, var(--mantine-color-yellow-light));
    border-bottom-color: light-dark(#ffec99, var(--mantine-color-yellow-light-hover));
  }

  &[data-tone='error'] {
    background: light-dark(#fff5f5, var(--mantine-color-red-light));
    border-bottom-color: light-dark(#ffc9c9, var(--mantine-color-red-light-hover));
  }

  &[data-tone='done'] {
    background: light-dark(#ebfbee, var(--mantine-color-green-light));
    border-bottom-color: light-dark(#d3f9d8, var(--mantine-color-green-light));
  }

  &[data-tone='offer'] {
    background: light-dark(#f8f9fa, var(--mantine-color-dark-6));
    border-bottom-color: light-dark(#e9ecef, var(--mantine-color-dark-5));
    color: light-dark(#495057, var(--mantine-color-dark-1));
  }

  .dot {
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #228be6;
  }

  &[data-tone='waiting'] .dot {
    background: #f59f00;
  }
  &[data-tone='error'] .dot {
    background: #e03131;
  }
  &[data-tone='done'] .dot {
    background: #2f9e44;
  }
  &[data-tone='offer'] .dot {
    background: light-dark(#adb5bd, var(--mantine-color-dark-2));
  }

  .what {
    font-weight: 600;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .period {
    color: light-dark(#5c6570, var(--mantine-color-dark-2));
    white-space: nowrap;
    flex: none;
  }

  em {
    font-style: normal;
    color: light-dark(#5c6570, var(--mantine-color-dark-2));
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    flex: none;
  }

  .plain {
    padding: 0;
    border: 0;
    background: none;
    font-size: 12px;
    color: light-dark(#495057, var(--mantine-color-dark-1));
    cursor: pointer;
    white-space: nowrap;
  }

  .plain:hover {
    text-decoration: underline;
  }

  .go {
    padding: 3px 14px;
    border: 0;
    border-radius: 4px;
    background: #228be6;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }

  .go:hover {
    background: #1c7ed6;
  }
`;

export const Fill = styled.span`
  position: absolute;
  left: 0;
  bottom: -1px;
  height: 3px;
  background: #228be6;
  transition: width 0.4s ease;
`;

export const Ask = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 14px;

  p {
    margin: 0;
    color: light-dark(#495057, var(--mantine-color-dark-1));
  }

  .sheets {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 168px;
    overflow-y: auto;
    border: 1px solid light-dark(#e9ecef, var(--mantine-color-dark-5));
    border-radius: 6px;
  }

  .sheets li {
    padding: 7px 10px;
    font-size: 13px;
    color: light-dark(#343a40, var(--mantine-color-dark-0));
    border-bottom: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sheets li:last-child {
    border-bottom: 0;
  }

  .sheets em {
    font-style: normal;
    color: light-dark(#868e96, var(--mantine-color-dark-2));
  }

  .sheets span {
    color: light-dark(#495057, var(--mantine-color-dark-1));
  }

  .sheets .more {
    color: light-dark(#868e96, var(--mantine-color-dark-2));
  }

  /* Four answers is what this question has when a re-extraction is on offer. */
  .buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 4px;
  }

  .buttons button {
    flex: none;
    white-space: nowrap;
  }

  .buttons .spacer {
    margin-left: auto;
  }

  .plain {
    padding: 0;
    border: 0;
    background: none;
    font-size: 13px;
    color: light-dark(#495057, var(--mantine-color-dark-1));
    cursor: pointer;
  }

  .plain:hover {
    text-decoration: underline;
  }
`;

/* The stage's own page, while the machine has it. No curtain. */
export const Working = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: calc(101px + var(--dx-run-band, 0px));
  z-index: 180;
  overflow: hidden;
  pointer-events: none;

  /* What the light does not say. */
  .pill {
    position: absolute;
    left: 50%;
    bottom: 28px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: min(640px, calc(100% - 48px));
    padding: 10px 16px;
    border-radius: 999px;
    border: 1px solid light-dark(#d0ebff, var(--mantine-color-blue-light));
    background: var(--mantine-color-body);
    box-shadow: 0 6px 20px rgba(16, 17, 19, 0.08);
    font-family: Roboto, sans-serif;
    pointer-events: auto;
    animation: ${rise} 260ms ease-out;
  }

  .what {
    font-size: 13px;
    font-weight: 600;
    color: light-dark(#101113, var(--mantine-color-dark-0));
    white-space: nowrap;
  }

  .count {
    font-size: 13px;
    color: light-dark(#495057, var(--mantine-color-dark-1));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pill[data-cta] {
    gap: 18px;
    padding: 16px 18px 16px 22px;
    border-radius: 14px;
    border-color: light-dark(#a5d8ff, var(--mantine-color-blue-light-hover));
    box-shadow: 0 12px 34px rgba(34, 139, 230, 0.18);
  }

  .pill[data-cta] .lines {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  /* The button says the one thing there is to do here; it does not get squeezed to "Open mappi"… */
  .pill[data-cta] button {
    flex: none;
    white-space: nowrap;
  }

  .pill[data-cta] .what {
    font-size: 15px;
    font-weight: 600;
    white-space: normal;
  }

  .pill[data-cta] em {
    font-style: normal;
    font-size: 12.5px;
    color: light-dark(#868e96, var(--mantine-color-dark-2));
    white-space: nowrap;
  }

  /* The one mark in this whole surface that says a thing went right. */
  .pill .tick {
    display: grid;
    place-items: center;
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #2f9e44;
    color: #ffffff;
  }

  .pill[data-done] {
    border-color: light-dark(#b2f2bb, var(--mantine-color-green-light-hover));
    box-shadow: 0 6px 20px rgba(47, 158, 68, 0.12);
  }

  .pill[data-done] .track i {
    background: #2f9e44;
  }

  .pill[data-done] .tile[data-state='done'] {
    background: #2f9e44;
  }

  .pill .plain {
    padding: 0;
    border: 0;
    background: none;
    font-size: 12px;
    color: light-dark(#495057, var(--mantine-color-dark-1));
    cursor: pointer;
    white-space: nowrap;
  }

  .pill .plain:hover {
    text-decoration: underline;
  }

  .tiles {
    display: flex;
    gap: 4px;
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
  }

  .tile {
    flex: 0 1 20px;
    min-width: 5px;
    height: 8px;
    border-radius: 2px;
    background: light-dark(#dbeafe, var(--mantine-color-blue-light));
  }

  .tile[data-state='done'] {
    background: #228be6;
  }

  .tile[data-state='warned'] {
    background: #f59f00;
  }

  .tile[data-state='failed'] {
    background: #e03131;
  }

  .tile[data-state='running'] {
    background: #74c0fc;
    animation: ${breathe} 1.4s ease-in-out infinite;
  }

  /* A stage with nothing to count still has to look like it is working. */
  .track {
    position: relative;
    width: 90px;
    height: 4px;
    border-radius: 2px;
    background: light-dark(#dbeafe, var(--mantine-color-blue-light));
    overflow: hidden;
    flex: none;
  }

  .track i {
    position: absolute;
    inset: 0 auto 0 0;
    display: block;
    border-radius: 2px;
    background: #228be6;
    transition: width 0.5s ease;
  }

  .track[data-indeterminate] i {
    width: 34%;
    animation: ${travel} 1.5s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .pill,
    .tile[data-state='running'],
    .track[data-indeterminate] i {
      animation: none;
    }

    /* And the bar has to still read as unfinished rather than as empty. */
    .track[data-indeterminate] i {
      width: 100%;
      background: repeating-linear-gradient(
        90deg,
        #228be6 0 6px,
        light-dark(#dbeafe, var(--mantine-color-blue-light)) 6px 12px
      );
    }
  }
`;

/* The closing account, in the same sheet the extraction and transformation lists use. */
export const Summary = styled.div`
  font-family: Roboto, sans-serif;
  color: light-dark(#343a40, var(--mantine-color-dark-0));

  .mid {
    max-width: 1080px;
    margin: 0 auto;
    width: 100%;
  }

  header {
    padding: 12px 24px;
    background: light-dark(#ebfbee, var(--mantine-color-green-light));
    border-bottom: 1px solid light-dark(#d3f9d8, var(--mantine-color-green-light));
  }

  header .mid {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* A real icon, centred by the box rather than by a font's idea of where a glyph sits. */
  header .tick {
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--mantine-color-body);
    border: 1.5px solid #40c057;
    color: #2f9e44;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  header .lines {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }

  header b {
    font-size: 14px;
    font-weight: 600;
    color: light-dark(#14532d, var(--mantine-color-green-light-color));
    letter-spacing: -0.005em;
  }

  header em {
    font-style: normal;
    font-size: 12px;
    color: light-dark(#2b8a3e, var(--mantine-color-green-light-color));
  }

  /* The app's own controls rather than bare buttons. */
  header .acts {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: none;
  }

  .body {
    padding: 18px 24px 22px;
  }

  h4 {
    margin: 0 0 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: light-dark(#adb5bd, var(--mantine-color-dark-2));
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .did {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
  }

  .did li {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 12px 6px;
    border-radius: 999px;
    background: light-dark(#f8f9fa, var(--mantine-color-dark-6));
    border: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
    white-space: nowrap;
  }

  /* Drawn rather than typed, and on its own disc. */
  .did i {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: light-dark(#d3f9d8, var(--mantine-color-green-light));
    color: light-dark(#2b8a3e, var(--mantine-color-green-light-color));
  }

  .did b {
    font-weight: 600;
    color: light-dark(#212529, var(--mantine-color-dark-0));
    font-variant-numeric: tabular-nums;
  }

  .did span {
    color: light-dark(#5c6570, var(--mantine-color-dark-2));
  }

  .left {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 18px;
    padding: 13px 16px;
    border-radius: 8px;
    background: light-dark(#f8f9fa, var(--mantine-color-dark-6));
    font-size: 13px;
    line-height: 1.45;
    color: light-dark(#495057, var(--mantine-color-dark-1));
  }

  .left li {
    display: flex;
    align-items: baseline;
    gap: 9px;
    max-width: 96ch;
  }

  .left li::before {
    content: '';
    flex: none;
    width: 5px;
    height: 5px;
    margin-top: 6px;
    border-radius: 50%;
    background: light-dark(#ced4da, var(--mantine-color-dark-3));
  }

  .left li[data-tone='bad'] {
    color: light-dark(#a51111, var(--mantine-color-red-light-color));
  }

  .left li[data-tone='bad']::before {
    background: #e03131;
  }

  .left b {
    font-weight: 600;
  }
`;
